import { useEffect, useState } from "react";
import {
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  BookOpen,
  AlertCircle,
  Trophy,
  Award,
  TrendingUp,
  ChevronRight,
  Star,
  Timer,
  HelpCircle,
  Zap,
  BarChart3,
  Calendar,
  PlayCircle,
  Lock,
  Sparkles,
} from "lucide-react";
import {
  quizApi,
  showError,
  showSuccess,
  EnrolledQuiz,
  QuizSubmitResponse,
} from "../../../lib";
import AiPrepModal from "../../quiz/AiPrepModal";

interface QuizWithDetails extends EnrolledQuiz {
  questions?: Array<{
    _id: string;
    question: string;
    options: string[];
    points?: number;
  }>;
  totalPoints?: number;
}

interface CourseQuizGroup {
  courseId: string;
  courseTitle: string;
  courseThumbnail?: string;
  quizzes: QuizWithDetails[];
  stats: {
    totalQuizzes: number;
    completedQuizzes: number;
    passedQuizzes: number;
    averageScore: number;
  };
}

const StudentQuizzes = () => {
  const [quizzes, setQuizzes] = useState<QuizWithDetails[]>([]);
  const [groupedQuizzes, setGroupedQuizzes] = useState<CourseQuizGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuiz, setSelectedQuiz] = useState<QuizWithDetails | null>(
    null,
  );
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<QuizSubmitResponse | null>(null);
  const [startTime, setStartTime] = useState<number>(0);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<"available" | "completed">(
    "available",
  );
  const [expandedCourse, setExpandedCourse] = useState<string | null>(null);
  const [showPrep, setShowPrep] = useState(false);
  const [currentPrepQuiz, setCurrentPrepQuiz] =
    useState<QuizWithDetails | null>(null);

  const getOptionId = (option: any, idx: number) =>
    typeof option === "string"
      ? option
      : (option?.id ?? option?.optionId ?? idx.toString());

  useEffect(() => {
    fetchQuizzes();
  }, []);

  useEffect(() => {
    const grouped = quizzes.reduce<CourseQuizGroup[]>((acc, quiz) => {
      const courseId = quiz.course._id;
      const existingGroup = acc.find((g) => g.courseId === courseId);
      if (existingGroup) {
        existingGroup.quizzes.push(quiz);
      } else {
        acc.push({
          courseId,
          courseTitle: quiz.course.title,
          courseThumbnail: quiz.course.thumbnail,
          quizzes: [quiz],
          stats: {
            totalQuizzes: 1,
            completedQuizzes: 0,
            passedQuizzes: 0,
            averageScore: 0,
          },
        });
      }
      return acc;
    }, []);

    grouped.forEach((group) => {
      const completed = group.quizzes.filter((q) => q.hasAttempted);
      const passed = group.quizzes.filter((q) => q.passed);
      const avgScore =
        completed.reduce((sum, q) => sum + (q.lastScore || 0), 0) /
        (completed.length || 1);

      group.stats = {
        totalQuizzes: group.quizzes.length,
        completedQuizzes: completed.length,
        passedQuizzes: passed.length,
        averageScore: Math.round(avgScore),
      };
    });

    setGroupedQuizzes(grouped);
  }, [quizzes]);

  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      const response = await quizApi.getEnrolledQuizzes();
      if (response.success) {
        setQuizzes(response.data || []);
      } else {
        const allQuizzesResponse = await quizApi.getAllMyQuizzes();
        if (allQuizzesResponse.success) {
          setQuizzes(allQuizzesResponse.data || []);
        }
      }
    } catch (err: any) {
      console.error("Failed to fetch quizzes:", err);
      showError("Failed to load quizzes");
    } finally {
      setLoading(false);
    }
  };

  const startQuiz = async (quiz: QuizWithDetails) => {
    try {
      const response = await quizApi.startQuiz(quiz._id);
      if (response.success) {
        const savedScore = (response.data as any)?.savedScore;
        setSelectedQuiz({
          ...quiz,
          questions: response.data?.questions || quiz.questions,
          totalPoints: response.data?.totalPoints || quiz.totalPoints,
        });
        setAnswers({});
        setStartTime(Date.now());
        setTimeRemaining(quiz.timeLimit);
        setShowPrep(false);
        if (savedScore) {
          setResult({
            score: {
              earned: savedScore.score,
              total: savedScore.score,
              percentage: savedScore.score,
            },
            passed: savedScore.passed || false,
            passingScore: quiz.passingScore,
            results: [],
            isCertificateExam: false,
          } as any);
        } else {
          setResult(null);
        }
      }
    } catch (err: any) {
      showError(err.message || "Failed to start quiz");
    }
  };

  const handleSubmitQuiz = async () => {
    if (!selectedQuiz) return;

    const totalQuestions = selectedQuiz.questions?.length || 0;
    const answeredCount = Object.keys(answers).length;

    if (answeredCount < totalQuestions) {
      showError(
        `Please answer all questions (${answeredCount}/${totalQuestions} answered)`,
      );
      return;
    }

    setSubmitting(true);
    try {
      const timeSpent = Math.floor((Date.now() - startTime) / 1000);
      const response = await quizApi.submitQuiz(
        selectedQuiz._id,
        answers,
        timeSpent,
      );
      if (response.success) {
        setResult(response.data);
        showSuccess(
          response.data.passed
            ? "Quiz passed! 🎉"
            : "Quiz completed. Keep practicing!",
        );
        fetchQuizzes();
      }
    } catch (err: any) {
      showError(err.message || "Failed to submit quiz");
    } finally {
      setSubmitting(false);
    }
  };

  const closeQuiz = () => {
    setSelectedQuiz(null);
    setAnswers({});
    setResult(null);
    setStartTime(0);
    setTimeRemaining(0);
    setShowPrep(false);
  };

  const formatTime = (minutes: number) => {
    const mins = Math.floor(minutes);
    const secs = Math.floor((minutes - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="w-10 h-10 text-orange-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading quizzes...</p>
        </div>
      </div>
    );
  }

  if (selectedQuiz && !result) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-gray-200">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold text-gray-900 truncate">
                {selectedQuiz.title}
              </h2>
              <p className="text-xs text-gray-500 truncate mt-0.5">
                {selectedQuiz.course.title}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <Timer className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-mono font-semibold">
                  {formatTime(timeRemaining)}
                </span>
              </div>
              <button
                onClick={closeQuiz}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Exit
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 max-h-[55vh] overflow-y-auto">
          <div className="space-y-5">
            {selectedQuiz.questions?.map((q, idx) => (
              <div
                key={q._id}
                className="border-b border-gray-100 pb-4 last:border-0"
              >
                <p className="text-sm font-medium text-gray-900 mb-2">
                  {idx + 1}. {q.question}
                  {q.points && (
                    <span className="ml-1 text-xs text-gray-400">
                      ({q.points} pts)
                    </span>
                  )}
                </p>
                <div className="space-y-2">
                  {q.options.map((option, optIdx) => {
                    const optionId =
                      typeof option === "string"
                        ? option
                        : (option.id ?? option.optionId ?? optIdx.toString());
                    const optionText =
                      typeof option === "string"
                        ? option
                        : (option.text ?? option.optionText ?? "");
                    return (
                      <label
                        key={optIdx}
                        className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-all cursor-pointer ${
                          answers[q.id] === optionId
                            ? "border-orange-500 bg-orange-50"
                            : "border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="radio"
                          name={q.id}
                          checked={answers[q.id] === optionId}
                          onChange={() =>
                            setAnswers({ ...answers, [q.id]: optionId })
                          }
                          className="w-4 h-4 text-orange-500 focus:ring-orange-200"
                        />
                        <span className="text-sm text-gray-700">
                          {optionText}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-500">
                Answered: {Object.keys(answers).length}/
                {selectedQuiz.questions?.length || 0}
              </p>
              <p className="text-sm text-gray-500">
                Time: {formatTime(timeRemaining)}
              </p>
            </div>
            <button
              onClick={handleSubmitQuiz}
              disabled={
                submitting ||
                Object.keys(answers).length !== selectedQuiz.questions?.length
              }
              className="w-full py-2.5 text-sm font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader className="w-4 h-4 animate-spin" />
                  Submitting...
                </span>
              ) : (
                `Submit Quiz (${Object.keys(answers).length}/${selectedQuiz.questions?.length})`
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (selectedQuiz && result) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-gray-200">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold text-gray-900 truncate">
                {selectedQuiz.title}
              </h2>
              <p className="text-xs text-gray-500 truncate mt-0.5">
                {selectedQuiz.course.title}
              </p>
            </div>
            <button
              onClick={closeQuiz}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Exit
            </button>
          </div>
        </div>

        <div className="p-5 text-center">
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
              result.passed ? "bg-green-100" : "bg-red-100"
            }`}
          >
            {result.passed ? (
              <Trophy className="w-10 h-10 text-green-600" />
            ) : (
              <XCircle className="w-10 h-10 text-red-600" />
            )}
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {result.passed ? "Quiz Passed! 🎉" : "Quiz Failed"}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Your score: {Math.round(result.score.percentage)}% (
            {result.score.earned}/{result.score.total} points)
          </p>

          {result.isCertificateExam && result.passed && result.certificate && (
            <div className="bg-amber-50 rounded-lg p-4 mb-5 border border-amber-200">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-amber-600" />
                <span className="text-sm font-semibold text-amber-800">
                  Certificate Earned!
                </span>
              </div>
              <p className="text-xs text-amber-700 mb-2">
                You've earned a certificate for passing this exam.
              </p>
              <button
                onClick={() =>
                  window.open(result.certificate?.verificationUrl, "_blank")
                }
                className="text-xs text-white bg-amber-600 px-3 py-1.5 rounded-md hover:bg-amber-700"
              >
                View Certificate
              </button>
            </div>
          )}

          <div className="flex gap-3 justify-center">
            <button
              onClick={() => setShowPrep(true)}
              className="px-4 py-2 text-sm bg-violet-50 text-violet-700 border border-violet-200 rounded-lg hover:bg-violet-100 inline-flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              AI Prep Drill
            </button>
            <button
              onClick={() => {
                setShowPrep(false);
                setResult(null);
                setStartTime(Date.now());
                setTimeRemaining(selectedQuiz.timeLimit);
                setAnswers({});
              }}
              className="px-4 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              Retake Quiz
            </button>
            <button
              onClick={closeQuiz}
              className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Back to Quizzes
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {groupedQuizzes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-base font-medium text-gray-900 mb-1">
            No Quizzes Available
          </h3>
          <p className="text-sm text-gray-500">
            Enroll in a course with quizzes to start practicing.
          </p>
        </div>
      ) : (
        groupedQuizzes.map((group) => (
          <div
            key={group.courseId}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden"
          >
            <div
              onClick={() =>
                setExpandedCourse(
                  expandedCourse === group.courseId ? null : group.courseId,
                )
              }
              className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                {group.courseThumbnail ? (
                  <img
                    src={group.courseThumbnail}
                    alt={group.courseTitle}
                    className="w-10 h-10 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-gray-400" />
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 truncate">
                    {group.courseTitle}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {group.stats.completedQuizzes}/{group.stats.totalQuizzes}{" "}
                    completed · {group.stats.averageScore}% avg
                  </p>
                </div>
              </div>
              <ChevronRight
                className={`w-5 h-5 text-gray-400 transition-transform ${
                  expandedCourse === group.courseId ? "rotate-90" : ""
                }`}
              />
            </div>

            {expandedCourse === group.courseId && (
              <div className="px-4 pb-4 space-y-3">
                {group.quizzes.map((quiz) => {
                  const isLocked =
                    quiz.hasAttempted &&
                    quiz.passed &&
                    (quiz.attempts || 0) >= (quiz.attemptsAllowed || 3);
                  const canRetake = quiz.hasAttempted && !isLocked;
                  const status = quiz.passed
                    ? "Passed"
                    : quiz.hasAttempted
                      ? "Attempted"
                      : "Not started";
                  const statusColor = quiz.passed
                    ? "text-green-600 bg-green-50"
                    : quiz.hasAttempted
                      ? "text-amber-600 bg-amber-50"
                      : "text-gray-500 bg-gray-50";

                  return (
                    <div
                      key={quiz._id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1 min-w-0 mr-3">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {quiz.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${statusColor}`}
                          >
                            {status}
                          </span>
                          {quiz.lastScore !== undefined && (
                            <span className="text-xs text-gray-500">
                              {quiz.lastScore}%
                            </span>
                          )}
                          <span className="text-xs text-gray-400">
                            {quiz.passingScore}% to pass · {quiz.timeLimit}min
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setCurrentPrepQuiz(quiz);
                            setShowPrep(true);
                          }}
                          className="text-xs text-violet-600 bg-violet-50 px-2.5 py-1.5 rounded-lg inline-flex items-center gap-1.5 hover:bg-violet-100 transition-colors"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          Prep
                        </button>

                        <button
                          onClick={() => startQuiz(quiz)}
                          disabled={isLocked}
                          className={`flex-shrink-0 ${isLocked ? "cursor-not-allowed opacity-50" : ""}`}
                        >
                          {isLocked ? (
                            <span className="flex items-center gap-1 text-xs text-gray-400">
                              <Lock className="w-3.5 h-3.5" />
                              Max
                            </span>
                          ) : canRetake ? (
                            <span className="flex items-center gap-1 text-xs text-orange-600">
                              <PlayCircle className="w-3.5 h-3.5" />
                              Retake
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-white bg-orange-500 px-3 py-1.5 rounded-lg hover:bg-orange-600 transition-colors">
                              <PlayCircle className="w-3.5 h-3.5" />
                              Start
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))
      )}

      {showPrep && currentPrepQuiz && (
        <AiPrepModal
          isOpen={showPrep}
          onClose={() => setShowPrep(false)}
          quizId={currentPrepQuiz._id}
          quizTitle={currentPrepQuiz.title}
          onRetakeReady={() => setShowPrep(false)}
        />
      )}
    </div>
  );
};

export default StudentQuizzes;
